import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { LocalizedDatePipe } from '@core/pipes/localize-date.pipe';
import type { ReminderTrackerBlockReason } from '@features/reminder-tracker/utils/reminder-tracker-block-reason.utility';
import {
  ReminderTrackerBlockReasonMap,
  resolveReminderTrackerBlockReason,
} from '@features/reminder-tracker/utils/reminder-tracker-block-reason.utility';
import { TranslocoDirective } from '@jsverse/transloco';
import type { DeliveryTrackerStatus } from '@kerberos-compliance/compliance-shared-ts/features/delivery/enums/delivery-tracker-status.enum';
import type { ReminderTrackerStatus } from '@kerberos-compliance/compliance-shared-ts/features/reminder-tracker/enums/reminder-tracker.enums';
import { ReminderTrackerStatusMap } from '@kerberos-compliance/compliance-shared-ts/features/reminder-tracker/enums/reminder-tracker.enums';
import { DateFormatConfigurations } from '@kerberos-compliance/compliance-shared-ui';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';

const StatusTagClass = {
  Pending: 'status-pending',
  Incomplete: 'status-incomplete',
  Complete: 'status-complete',
  Missing: 'status-missing',
  Blocked: 'status-blocked',
} as const;

const StatusLabelKey = {
  Pending: 'pending',
  Sent: 'sent',
  Done: 'done',
  Missing: 'missing',
} as const;

const TooltipKey = {
  LastUpdated: 'tooltip',
  Missing: 'missingTooltip',
} as const;

const ReminderTrackerStatusClassMap: Record<ReminderTrackerStatus, string> = {
  [ReminderTrackerStatusMap.Pending]: StatusTagClass.Pending,
  [ReminderTrackerStatusMap.Sent]: StatusTagClass.Incomplete,
  [ReminderTrackerStatusMap.Done]: StatusTagClass.Complete,
};

const ReminderTrackerStatusLabelKeyMap: Record<ReminderTrackerStatus, string> = {
  [ReminderTrackerStatusMap.Pending]: StatusLabelKey.Pending,
  [ReminderTrackerStatusMap.Sent]: StatusLabelKey.Sent,
  [ReminderTrackerStatusMap.Done]: StatusLabelKey.Done,
};

type BlockReason = Exclude<ReminderTrackerBlockReason, typeof ReminderTrackerBlockReasonMap.None>;

const ReminderTrackerBlockReasonLabelKeyMap: Record<BlockReason, string> = {
  [ReminderTrackerBlockReasonMap.InactiveContract]: 'contractInactive',
  [ReminderTrackerBlockReasonMap.NoActiveUser]: 'noActiveUser',
};

const ReminderTrackerBlockReasonTooltipKeyMap: Record<BlockReason, string> = {
  [ReminderTrackerBlockReasonMap.InactiveContract]: 'contractInactiveTooltip',
  [ReminderTrackerBlockReasonMap.NoActiveUser]: 'noActiveUserTooltip',
};

@Component({
  selector: 'aad-reminder-tracker-presence-badge',
  imports: [NzTagComponent, NzTooltipDirective, TranslocoDirective, LocalizedDatePipe],
  templateUrl: 'reminder-tracker-presence-badge.component.html',
  styleUrl: 'reminder-tracker-presence-badge.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReminderTrackerPresenceBadgeComponent {
  public readonly reminderTrackerStatus = input<ReminderTrackerStatus | undefined>();

  public readonly deliveryTrackerStatus = input<DeliveryTrackerStatus | undefined>();

  public readonly contractInactive = input<boolean>(false);

  public readonly reminderTrackerCreatedAt = input<string | Date | undefined>();

  public readonly reminderTrackerUpdatedAt = input<string | Date | undefined>();

  public readonly reminderTrackerNextReminderAt = input<string | Date | null | undefined>();

  public readonly reminderTrackerStatusChangedAt = input<string | Date | null | undefined>();

  public readonly hasActiveNotifiableUser = input<boolean>(true);

  protected readonly blockReason = computed<ReminderTrackerBlockReason>(() =>
    resolveReminderTrackerBlockReason({
      reminderTrackerStatus: this.reminderTrackerStatus(),
      deliveryTrackerStatus: this.deliveryTrackerStatus(),
      isContractInactive: this.contractInactive(),
      hasActiveNotifiableUser: this.hasActiveNotifiableUser(),
    }),
  );

  protected readonly isBlocked = computed(() => this.blockReason() !== ReminderTrackerBlockReasonMap.None);

  protected readonly statusClass = computed(() => {
    if (this.isBlocked()) {
      return StatusTagClass.Blocked;
    }
    const status = this.reminderTrackerStatus();
    if (status !== undefined) {
      return ReminderTrackerStatusClassMap[status];
    }
    return StatusTagClass.Missing;
  });

  protected readonly statusLabelKey = computed(() => {
    const reason = this.blockReason();
    if (reason !== ReminderTrackerBlockReasonMap.None) {
      return ReminderTrackerBlockReasonLabelKeyMap[reason];
    }
    const status = this.reminderTrackerStatus();
    if (status !== undefined) {
      return ReminderTrackerStatusLabelKeyMap[status];
    }
    return StatusLabelKey.Missing;
  });

  protected readonly showCreatedAt = computed(
    () => !this.isBlocked() && this.reminderTrackerStatus() !== undefined && !!this.reminderTrackerCreatedAt(),
  );

  protected readonly nextReminderAt = computed(() => {
    if (this.isBlocked()) {
      return;
    }
    const status = this.reminderTrackerStatus();
    if (status === undefined || status === ReminderTrackerStatusMap.Done) {
      return;
    }
    return this.reminderTrackerNextReminderAt() ?? undefined;
  });

  protected readonly completedAt = computed(() => {
    if (this.reminderTrackerStatus() !== ReminderTrackerStatusMap.Done) {
      return;
    }
    return this.reminderTrackerStatusChangedAt() ?? undefined;
  });

  protected readonly tooltipKey = computed(() => {
    const reason = this.blockReason();
    if (reason !== ReminderTrackerBlockReasonMap.None) {
      return ReminderTrackerBlockReasonTooltipKeyMap[reason];
    }
    const status = this.reminderTrackerStatus();
    if (status !== undefined) {
      return this.reminderTrackerUpdatedAt() ? TooltipKey.LastUpdated : undefined;
    }
    return TooltipKey.Missing;
  });

  protected readonly showDateTooltip = computed(() => this.tooltipKey() === TooltipKey.LastUpdated);

  protected readonly dateTimeFormat = DateFormatConfigurations.dateTimeFormat;

  protected readonly dateFormat = DateFormatConfigurations.dateFormat;
}
